import React, { useState, useEffect } from 'react';
import { nextFiveAM } from './channelSnoozePresets';
import { useInvalidateStores } from '../../hooks/queries';
import { useProductVariantScope } from '../../hooks/useProductVariantScope';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { DayOfWeek } from '../StoreHours/types';
import {
  createItemBlackout,
  bulkDeleteItemBlackouts,
  getAvailableChannels,
  bulkUpdateStoreHours,
  bulkUpdateChannels,
  bulkAddSpecialHours,
  retryBulkStoreHours,
  retryBulkChannels,
  updateStoreOnlineOrders,
  type AvailableChannel,
  type BulkOperationResponse,
  type BulkOperationStatus,
} from '../../api';
import { useTimeFormat } from '../../context/TimeFormatContext';
import { formatSnoozeDateTime } from '../../utils/formatTime';

// Import sub-components
import {
  type BulkOperationType,
  type BulkChannelsData,
  type SpecialHoursEntry,
  type SelectedItem,
  type UnifiedHoursState,
  type OccasionTypeFromTypes,
  BulkOperationResults,
  BulkHoursEditor,
  BulkSpecialHoursEditor,
  BulkChannelsEditor,
  BulkItemsEditor,
} from './BulkOperations';

interface BulkOperationsDialogProps {
  open: boolean;
  onClose: () => void;
  selectedStoresCount: number;
  selectedStoreNumbers: string[];
  onApply: (operation: string, data: any) => Promise<void>;
  operationType: BulkOperationType;
  onSuccess?: (message: string) => void;
}

export const BulkOperationsDialog: React.FC<BulkOperationsDialogProps> = ({
  open,
  onClose,
  selectedStoresCount,
  selectedStoreNumbers,
  onApply: _onApply,
  operationType,
  onSuccess,
}) => {
  const invalidateStores = useInvalidateStores();
  const { use24HourFormat } = useTimeFormat();
  const [isApplying, setIsApplying] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({ open: false, title: '', message: '' });

  // Bulk operation result state
  const [operationResult, setOperationResult] = useState<BulkOperationResponse | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [showResultDetails, setShowResultDetails] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  // Available channels state
  const [availableChannels, setAvailableChannels] = useState<AvailableChannel[]>([]);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);

  // Fetch available channels when dialog opens for channels operation
  useEffect(() => {
    if (open && operationType === 'channels') {
      setIsLoadingChannels(true);
      getAvailableChannels()
        .then((channels) => {
          setAvailableChannels(channels);
          // Initialize aggregators state with fetched channels
          // All start as ON (enabled) but unchecked (include: false)
          const aggregatorsState: BulkChannelsData['aggregators'] = {};
          const originalState: Record<string, boolean> = {};
          channels.forEach((ch) => {
            aggregatorsState[ch.channel] = { enabled: true, include: false };
            originalState[ch.channel] = true;
          });
          setChannelsData({
            acceptingOnlineOrders: true,
            includeOnlineOrders: false,  // Reset checkbox to unchecked
            aggregators: aggregatorsState,
          });
          setOriginalChannelState(originalState);
        })
        .catch((err) => {
          console.error('Failed to fetch available channels:', err);
        })
        .finally(() => {
          setIsLoadingChannels(false);
        });
    }
  }, [open, operationType]);

  // Store Hours state
  const [unifiedHoursState, setUnifiedHoursState] = useState<UnifiedHoursState | null>(null);

  // Channels state
  const [channelsData, setChannelsData] = useState<BulkChannelsData>({
    acceptingOnlineOrders: true,
    aggregators: {},
  });
  const [originalChannelState, setOriginalChannelState] = useState<Record<string, boolean>>({});

  // Items state
  const [itemsOperation, setItemsOperation] = useState<'snooze' | 'activate'>('snooze');
  const [itemType, setItemType] = useState<'PRODUCT' | 'BUNDLE' | 'VARIANT' | 'MODIFIER' | 'OPTION_VALUE'>('PRODUCT');
  // Reason picker removed from UI (only OUT_OF_STOCK is selectable). Value is
  // still required by the backend payload.
  const itemReason = 'OUT_OF_STOCK' as const;
  const getEndOfDay = () => {
    return nextFiveAM();
  };
  const [itemExpiry, setItemExpiry] = useState<Date | null>(getEndOfDay());
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

  // Variant drill-down for bulk item operations. Same flow for snooze and
  // activate — pick a product with multiple variants and choose between
  // whole-product or specific-variants scope.
  const variantScope = useProductVariantScope({
    isProductType: itemType === 'PRODUCT',
    selectedItems,
  });

  // Special hours state
  const [specialHoursEntries, setSpecialHoursEntries] = useState<SpecialHoursEntry[]>([]);

  const handleClose = () => {
    // Reset all state
    setSelectedItems([]);
    setItemExpiry(getEndOfDay());
    setUnifiedHoursState(null);
    setSpecialHoursEntries([]);
    setOperationResult(null);
    setShowResults(false);
    setShowResultDetails(false);
    setIsRetrying(false);
    // Reset channels data - checkboxes will be re-initialized on next open
    setChannelsData({
      acceptingOnlineOrders: true,
      includeOnlineOrders: false,
      aggregators: {},
    });
    onClose();
  };

  // Show confirmation dialog before applying
  const handleApplyClick = () => {
    let title = 'Confirm Bulk Update';
    let message = '';

    switch (operationType) {
      case 'hours':
        title = 'Confirm Bulk Hours Update';
        message = `You are about to update store hours for ${selectedStoresCount} store${selectedStoresCount !== 1 ? 's' : ''}. This will overwrite existing hours for the selected occasions.`;
        break;
      case 'channels': {
        title = 'Confirm Bulk Channel Update';

        // Only include items that have been checked
        const includedChannels = Object.entries(channelsData.aggregators).filter(
          ([, data]) => data.include === true
        );
        const includeOnlineOrders = channelsData.includeOnlineOrders === true;

        // Build descriptive message - only show checked items
        const updates: string[] = [];

        if (includeOnlineOrders) {
          const offWithReactivation =
            channelsData.acceptingOnlineOrders === false && channelsData.onlineOrdersReactivateAt
              ? ` (automatically back ON at ${formatSnoozeDateTime(channelsData.onlineOrdersReactivateAt, use24HourFormat)})`
              : '';
          updates.push(`Online Orders ${channelsData.acceptingOnlineOrders ? 'ON' : 'OFF'}${offWithReactivation}`);
        }

        if (includedChannels.length > 0) {
          const turningOn = includedChannels.filter(([, data]) => data.enabled).length;
          const turningOff = includedChannels.filter(([, data]) => !data.enabled).length;
          if (turningOn > 0) updates.push(`${turningOn} delivery channel${turningOn !== 1 ? 's' : ''} ON`);
          if (turningOff > 0) updates.push(`${turningOff} delivery channel${turningOff !== 1 ? 's' : ''} OFF`);
        }

        if (updates.length === 0) {
          message = 'No items selected for update. Please check at least one item to modify.';
        } else {
          message = `You are about to set ${updates.join(', ')} for ${selectedStoresCount} store${selectedStoresCount !== 1 ? 's' : ''}.`;
        }
        break;
      }
      case 'special-hours':
        title = 'Confirm Bulk Special Hours';
        message = `You are about to add ${specialHoursEntries.length} special hours entr${specialHoursEntries.length !== 1 ? 'ies' : 'y'} to ${selectedStoresCount} store${selectedStoresCount !== 1 ? 's' : ''}.`;
        break;
      case 'items':
        title = itemsOperation === 'snooze' ? 'Confirm Bulk Item Snooze' : 'Confirm Bulk Item Activation';
        message = `You are about to ${itemsOperation} ${selectedItems.length} item${selectedItems.length !== 1 ? 's' : ''} across ${selectedStoresCount} store${selectedStoresCount !== 1 ? 's' : ''}.`;
        break;
    }

    setConfirmDialog({ open: true, title, message });
  };

  const handleApply = async () => {
    setConfirmDialog({ open: false, title: '', message: '' });
    setIsApplying(true);
    setOperationResult(null);
    setShowResults(false);

    try {
      switch (operationType) {
        case 'hours': {
          if (!unifiedHoursState) {
            throw new Error('Hours configuration is required');
          }

          const { primarySchedule, occasionSettings } = unifiedHoursState;
          const occasionsToUpdate = Object.entries(occasionSettings) as [OccasionTypeFromTypes, typeof occasionSettings[OccasionTypeFromTypes]][];

          if (occasionsToUpdate.length === 0) {
            throw new Error('At least one occasion must be configured');
          }

          let combinedResult: BulkOperationResponse | null = null;
          let totalSucceeded = 0;
          let totalFailed = 0;
          let totalTotal = 0;

          for (const [occasion, settings] of occasionsToUpdate) {
            const scheduleToUse = settings.mode === 'inherit' || !settings.customSchedule
              ? primarySchedule
              : settings.customSchedule;

            const daysConfig: Record<DayOfWeek, { enabled: boolean; timeSlots: Array<{ open: string; close: string }> }> = {} as any;
            for (const day of Object.keys(scheduleToUse) as DayOfWeek[]) {
              const daySchedule = scheduleToUse[day];
              daysConfig[day] = {
                enabled: daySchedule.enabled,
                timeSlots: daySchedule.enabled && daySchedule.timeSlots.length === 0
                  ? [{ open: '09:00', close: '22:00' }]
                  : daySchedule.timeSlots,
              };
            }

            const result = await bulkUpdateStoreHours({
              storeNumbers: selectedStoreNumbers,
              occasion,
              days: daysConfig,
            });

            totalSucceeded += result.summary.succeeded;
            totalFailed += result.summary.failed;
            totalTotal += result.summary.total;

            if (!combinedResult || result.status === 'failure' ||
                (result.status === 'partial_success' && combinedResult.status === 'success')) {
              combinedResult = result;
            }
          }

          if (combinedResult && occasionsToUpdate.length > 1) {
            combinedResult = {
              ...combinedResult,
              summary: {
                ...combinedResult.summary,
                total: totalTotal,
                succeeded: totalSucceeded,
                failed: totalFailed,
              },
            };
          }

          const result = combinedResult!;
          setOperationResult(result);
          setShowResults(true);

          if (result.status === 'success' || result.status === 'partial_success') {
            invalidateStores();
          }

          const occasionCount = occasionsToUpdate.length;
          const occasionText = occasionCount > 1 ? `${occasionCount} occasions` : 'hours';
          if (result.status === 'success') {
            onSuccess?.(`Successfully updated ${occasionText} for ${selectedStoreNumbers.length} store${selectedStoreNumbers.length !== 1 ? 's' : ''}`);
            setTimeout(() => handleClose(), 1500);
          } else if (result.status === 'partial_success') {
            onSuccess?.(`Updated ${occasionText}: ${totalSucceeded} succeeded, ${totalFailed} failed.`);
          }
          break;
        }

        case 'special-hours': {
          if (!specialHoursEntries || specialHoursEntries.length === 0) {
            throw new Error('At least one special hours entry is required');
          }

          let combinedResult: BulkOperationResponse | null = null;

          for (const entry of specialHoursEntries) {
            for (const occasion of entry.occasions) {
              const result = await bulkAddSpecialHours({
                storeNumbers: selectedStoreNumbers,
                occasion,
                entries: [{
                  // startDate/endDate are already in YYYY-MM-DD format, no parsing needed
                  startDate: entry.startDate,
                  endDate: entry.endDate,
                  isClosed: entry.isClosed,
                  openTime: entry.isClosed ? undefined : entry.openTime,
                  closeTime: entry.isClosed ? undefined : entry.closeTime,
                }],
              });

              if (!combinedResult || result.status === 'failure' ||
                  (result.status === 'partial_success' && combinedResult.status === 'success')) {
                combinedResult = result;
              }
            }
          }

          if (combinedResult) {
            setOperationResult(combinedResult);
            setShowResults(true);

            if (combinedResult.status === 'success' || combinedResult.status === 'partial_success') {
              invalidateStores();
            }

            if (combinedResult.status === 'success') {
              onSuccess?.(`Successfully added special hours to ${selectedStoreNumbers.length} store${selectedStoreNumbers.length !== 1 ? 's' : ''}`);
              setTimeout(() => handleClose(), 1500);
            } else if (combinedResult.status === 'partial_success') {
              onSuccess?.(`Added special hours: ${combinedResult.summary.succeeded} succeeded, ${combinedResult.summary.failed} failed.`);
            }
          }
          break;
        }

        case 'channels': {
          // Only process items that have been explicitly checked
          const includedChannels = Object.entries(channelsData.aggregators).filter(
            ([, data]) => data.include === true
          );
          const includeOnlineOrders = channelsData.includeOnlineOrders === true;
          // Staging Online Orders OFF: every store gets the same auto-reactivation
          // time, and the force-paused aggregators inherit it as their pause
          // expiry so Byte Graph resumes everything together.
          const forcingOnlineOrdersOff =
            includeOnlineOrders && channelsData.acceptingOnlineOrders === false;
          const ooReactivateAtIso = forcingOnlineOrdersOff
            ? channelsData.onlineOrdersReactivateAt?.toISOString()
            : undefined;

          let combinedResult: BulkOperationResponse | null = null;
          let onlineOrdersSucceeded = 0;
          let onlineOrdersFailed = 0;
          const messages: string[] = [];

          // Only apply Online Orders if checkbox is checked
          if (includeOnlineOrders) {
            const onlineOrdersResults = await Promise.allSettled(
              selectedStoreNumbers.map(storeNumber =>
                updateStoreOnlineOrders(storeNumber, {
                  acceptingOnlineOrders: channelsData.acceptingOnlineOrders,
                  reactivateAt: ooReactivateAtIso,
                })
              )
            );

            onlineOrdersResults.forEach((result) => {
              if (result.status === 'fulfilled' && result.value?.status) {
                onlineOrdersSucceeded++;
              } else {
                onlineOrdersFailed++;
              }
            });

            if (onlineOrdersFailed === 0) {
              messages.push(`Online Orders ${channelsData.acceptingOnlineOrders ? 'enabled' : 'disabled'} for ${onlineOrdersSucceeded} store${onlineOrdersSucceeded !== 1 ? 's' : ''}`);
            } else {
              messages.push(`Online Orders: ${onlineOrdersSucceeded} succeeded, ${onlineOrdersFailed} failed`);
            }
          }

          // Only process delivery channels that are checked. Cascade pauses
          // (forced OFF because Online Orders is being disabled) inherit the
          // Online Orders reactivate-at time as their expiry.
          for (const [channel, channelData] of includedChannels) {
            const isCascadePause = forcingOnlineOrdersOff && !channelData.enabled;
            const result = await bulkUpdateChannels({
              storeNumbers: selectedStoreNumbers,
              channel,
              activate: channelData.enabled,
              deactivationReason: isCascadePause
                ? 'Online Orders disabled'
                : channelData.deactivationReason,
              deactivationExpiry: isCascadePause
                ? ooReactivateAtIso
                : channelData.deactivationExpiry?.toISOString(),
            });

            combinedResult = result;
          }

          if (combinedResult) {
            setOperationResult(combinedResult);
            setShowResults(true);

            if (combinedResult.status === 'success' || combinedResult.status === 'partial_success') {
              invalidateStores();
            }

            messages.push(`Delivery channel changes submitted. May take a few minutes to reflect.`);
          } else if (includeOnlineOrders) {
            // Only Online Orders was applied, no delivery channel changes
            invalidateStores();
          }

          const hasFailures = onlineOrdersFailed > 0 || (combinedResult?.status === 'partial_success');
          if (messages.length > 0) {
            onSuccess?.(messages.join('. '));
            if (!hasFailures) {
              setTimeout(() => handleClose(), 2000);
            }
          }
          break;
        }

        case 'items': {
          if (itemsOperation === 'snooze') {
            // Applies each product's variant scope: products narrowed to
            // specific variants send only variant codes, leaving the parent
            // available so sibling variants stay orderable across all stores.
            // `auditItems` carries display names for the activity log.
            const {
              productCodes,
              bundleCodes,
              variantCodes,
              modifierCodes,
              optionValueCodes,
              auditItems,
            } = variantScope.buildSubmitPayload(selectedItems);

            await createItemBlackout(
              selectedStoreNumbers,
              productCodes,
              bundleCodes,
              itemReason,
              itemExpiry?.toISOString() || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
              undefined,
              variantCodes,
              modifierCodes,
              optionValueCodes,
              auditItems
            );
            const snoozedCount = variantScope.getSnoozeUnitCount(selectedItems);
            onSuccess?.(`Successfully snoozed ${snoozedCount} item${snoozedCount !== 1 ? 's' : ''} across ${selectedStoreNumbers.length} store${selectedStoreNumbers.length !== 1 ? 's' : ''}`);
            handleClose();
            return;
          } else {
            // Same split as the snooze path. A product narrowed to specific
            // variants only clears variant-level blackouts — if the whole
            // product was snoozed, the user must pick Whole product instead.
            const {
              productCodes,
              bundleCodes,
              variantCodes,
              modifierCodes,
              optionValueCodes,
              auditItems,
            } = variantScope.buildSubmitPayload(selectedItems);

            const deleteResult = await bulkDeleteItemBlackouts({
              storeNumbers: selectedStoreNumbers,
              productCodes,
              bundleCodes,
              variantCodes,
              modifierCodes,
              optionValueCodes,
              items: auditItems,
            });

            const activatedCount = variantScope.getSnoozeUnitCount(selectedItems);

            if (deleteResult.status) {
              onSuccess?.(`Successfully activated ${activatedCount} item${activatedCount !== 1 ? 's' : ''} across ${deleteResult.summary.succeeded} store${deleteResult.summary.succeeded !== 1 ? 's' : ''}`);
              handleClose();
            } else {
              setOperationResult({
                status: deleteResult.summary.succeeded > 0 ? 'partial_success' : 'failure',
                summary: {
                  total: deleteResult.summary.total,
                  succeeded: deleteResult.summary.succeeded,
                  failed: deleteResult.summary.failed,
                  retryable: 0,
                },
                results: deleteResult.results.map(r => ({
                  storeNumber: r.storeNumber,
                  success: r.success,
                  error: r.error ? { message: r.error, category: 'PERMANENT' as const } : undefined,
                })),
                failedStoreNumbers: deleteResult.results.filter(r => !r.success).map(r => r.storeNumber),
                retryableStoreNumbers: [],
                requestId: '',
              });
              setShowResults(true);
            }
          }
          break;
        }
      }
    } catch (error) {
      console.error('Failed to apply bulk operation:', error);
      setOperationResult({
        status: 'failure',
        summary: {
          total: selectedStoreNumbers.length,
          succeeded: 0,
          failed: selectedStoreNumbers.length,
          retryable: 0,
        },
        results: selectedStoreNumbers.map(sn => ({
          storeNumber: sn,
          success: false,
          error: {
            message: error instanceof Error ? error.message : 'Unknown error',
            category: 'PERMANENT' as const,
          },
        })),
        failedStoreNumbers: selectedStoreNumbers,
        retryableStoreNumbers: [],
        requestId: 'error-' + Date.now(),
      });
      setShowResults(true);
    } finally {
      setIsApplying(false);
    }
  };

  const handleRetry = async () => {
    if (!operationResult || operationResult.retryableStoreNumbers.length === 0) return;

    setIsRetrying(true);
    try {
      let result: BulkOperationResponse | null = null;

      switch (operationType) {
        case 'hours': {
          if (!unifiedHoursState) break;

          const { primarySchedule, occasionSettings } = unifiedHoursState;
          const occasionsToRetry = Object.entries(occasionSettings) as [OccasionTypeFromTypes, typeof occasionSettings[OccasionTypeFromTypes]][];

          for (const [occasion, settings] of occasionsToRetry) {
            const scheduleToUse = settings.mode === 'inherit' || !settings.customSchedule
              ? primarySchedule
              : settings.customSchedule;

            const daysConfig: Record<DayOfWeek, { enabled: boolean; timeSlots: Array<{ open: string; close: string }> }> = {} as any;
            for (const day of Object.keys(scheduleToUse) as DayOfWeek[]) {
              const daySchedule = scheduleToUse[day];
              daysConfig[day] = {
                enabled: daySchedule.enabled,
                timeSlots: daySchedule.enabled && daySchedule.timeSlots.length === 0
                  ? [{ open: '09:00', close: '22:00' }]
                  : daySchedule.timeSlots,
              };
            }

            result = await retryBulkStoreHours(
              operationResult.requestId,
              operationResult.retryableStoreNumbers,
              occasion,
              daysConfig
            );
          }
          break;
        }

        case 'channels': {
          const changedChannel = Object.entries(channelsData.aggregators).find(
            ([channel, data]) => data.enabled !== originalChannelState[channel]
          );

          if (changedChannel) {
            const [channel, channelData] = changedChannel;
            result = await retryBulkChannels(
              operationResult.requestId,
              operationResult.retryableStoreNumbers,
              channel,
              channelData.enabled,
              channelData.deactivationReason,
              channelData.deactivationExpiry?.toISOString()
            );
          }
          break;
        }
      }

      if (result) {
        const updatedResults = [...operationResult.results];
        for (const retryResult of result.results) {
          const existingIndex = updatedResults.findIndex(
            r => r.storeNumber === retryResult.storeNumber
          );
          if (existingIndex >= 0) {
            updatedResults[existingIndex] = retryResult;
          }
        }

        const succeeded = updatedResults.filter(r => r.success).length;
        const failed = updatedResults.filter(r => !r.success).length;
        const retryable = updatedResults.filter(
          r => !r.success && r.error?.category === 'TRANSIENT'
        ).length;

        const newStatus: BulkOperationStatus =
          failed === 0 ? 'success' :
          succeeded === 0 ? 'failure' : 'partial_success';

        setOperationResult({
          ...operationResult,
          status: newStatus,
          summary: {
            total: updatedResults.length,
            succeeded,
            failed,
            retryable,
          },
          results: updatedResults,
          failedStoreNumbers: updatedResults.filter(r => !r.success).map(r => r.storeNumber),
          retryableStoreNumbers: updatedResults
            .filter(r => !r.success && r.error?.category === 'TRANSIENT')
            .map(r => r.storeNumber),
        });

        if (newStatus === 'success' || newStatus === 'partial_success') {
          invalidateStores();
        }

        if (newStatus === 'success') {
          onSuccess?.(`Retry successful! All ${succeeded} stores updated.`);
          setTimeout(() => handleClose(), 1500);
        } else if (newStatus === 'partial_success') {
          onSuccess?.(`Retry completed. ${succeeded} stores updated, ${failed} still failed.`);
        }
      }
    } catch (error) {
      console.error('Failed to retry bulk operation:', error);
    } finally {
      setIsRetrying(false);
    }
  };

  const getDialogTitle = () => {
    switch (operationType) {
      case 'hours': return 'Bulk Edit Store Hours';
      case 'channels': return 'Bulk Edit Channels';
      case 'special-hours': return 'Bulk Add Special Hours';
      case 'items': return 'Bulk Snooze Items';
    }
  };

  const isApplyDisabled = () => {
    if (isApplying) return true;
    if (operationType === 'items' && selectedItems.length === 0) return true;
    if (operationType === 'items' && variantScope.hasInvalidVariantScope) return true;
    if (operationType === 'special-hours' && specialHoursEntries.length === 0) return true;
    if (operationType === 'channels') {
      // Disable if no items are checked
      const includedChannels = Object.values(channelsData.aggregators).filter(data => data.include === true);
      const includeOnlineOrders = channelsData.includeOnlineOrders === true;
      if (includedChannels.length === 0 && !includeOnlineOrders) return true;
      // Disabling Online Orders requires a reactivate-at time (never indefinite)
      if (
        includeOnlineOrders &&
        channelsData.acceptingOnlineOrders === false &&
        !channelsData.onlineOrdersReactivateAt
      ) {
        return true;
      }
    }
    return false;
  };

  // Helper to count checked channel items
  const getCheckedChannelCount = () => {
    const includedChannels = Object.values(channelsData.aggregators).filter(data => data.include === true);
    const includeOnlineOrders = channelsData.includeOnlineOrders === true;
    return includedChannels.length + (includeOnlineOrders ? 1 : 0);
  };

  const getApplyButtonText = () => {
    if (isApplying) {
      return (
        <>
          <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
          Applying...
        </>
      );
    }
    if (operationType === 'items' && selectedItems.length > 0) {
      return `Apply ${selectedItems.length} Item${selectedItems.length !== 1 ? 's' : ''} to ${selectedStoresCount} Store${selectedStoresCount !== 1 ? 's' : ''}`;
    }
    if (operationType === 'special-hours' && specialHoursEntries.length > 0) {
      return `Add ${specialHoursEntries.length} Special Hours to ${selectedStoresCount} Store${selectedStoresCount !== 1 ? 's' : ''}`;
    }
    if (operationType === 'channels') {
      const checkedCount = getCheckedChannelCount();
      if (checkedCount === 0) {
        return 'Select items to apply';
      }
      return `Apply ${checkedCount} Item${checkedCount !== 1 ? 's' : ''} to ${selectedStoresCount} Store${selectedStoresCount !== 1 ? 's' : ''}`;
    }
    return `Apply to ${selectedStoresCount} Store${selectedStoresCount !== 1 ? 's' : ''}`;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box>
          <Typography variant="h6">{getDialogTitle()}</Typography>
          <Typography variant="body2" color="text.secondary">
            Apply changes to {selectedStoresCount} selected store{selectedStoresCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Store Hours Content */}
        {operationType === 'hours' && (
          <BulkHoursEditor onChange={setUnifiedHoursState} />
        )}

        {/* Special Hours Content */}
        {operationType === 'special-hours' && (
          <BulkSpecialHoursEditor
            entries={specialHoursEntries}
            onEntriesChange={setSpecialHoursEntries}
          />
        )}

        {/* Channels Content */}
        {operationType === 'channels' && (
          <BulkChannelsEditor
            channelsData={channelsData}
            onChannelsDataChange={setChannelsData}
            availableChannels={availableChannels}
            isLoadingChannels={isLoadingChannels}
            originalChannelState={originalChannelState}
          />
        )}

        {/* Items Content */}
        {operationType === 'items' && (
          <BulkItemsEditor
            itemsOperation={itemsOperation}
            onOperationChange={setItemsOperation}
            selectedItems={selectedItems}
            onSelectedItemsChange={setSelectedItems}
            itemType={itemType}
            onItemTypeChange={setItemType}
            itemExpiry={itemExpiry}
            onItemExpiryChange={setItemExpiry}
            variantScope={variantScope}
          />
        )}

        {/* Results Section */}
        {showResults && operationResult && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <BulkOperationResults
              operationResult={operationResult}
              showResultDetails={showResultDetails}
              onToggleDetails={() => setShowResultDetails(!showResultDetails)}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isApplying || isRetrying}>
          {showResults ? 'Close' : 'Cancel'}
        </Button>

        {/* Retry Button */}
        {showResults && operationResult && operationResult.retryableStoreNumbers.length > 0 && (
          <Button
            onClick={handleRetry}
            variant="outlined"
            color="warning"
            disabled={isRetrying}
            startIcon={isRetrying ? <CircularProgress size={16} /> : <RefreshIcon />}
          >
            {isRetrying ? 'Retrying...' : `Retry ${operationResult.retryableStoreNumbers.length} Failed`}
          </Button>
        )}

        {/* Apply Button */}
        {!showResults && (
          <Button
            onClick={handleApplyClick}
            variant="contained"
            disabled={isApplyDisabled()}
          >
            {getApplyButtonText()}
          </Button>
        )}
      </DialogActions>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {confirmDialog.message}
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, title: '', message: '' })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            color="primary"
          >
            Apply Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
